import { useCallback, useEffect, useMemo, useState, type FormEvent, type MouseEvent } from "react";
import {
  Badge,
  Card,
  CheckboxInput,
  DataTable,
  EmptyState,
  PageHeader,
  SelectInput,
  TextInput,
  type DataTableColumn,
  type ProjectWorkItem,
} from "@pericles/framework-frontend";
import { WORK_STATUSES, type WorkStatus } from "@pericles/shared";

import type { JarvisRunRecord, JarvisRunStatus } from "../types/jarvis";
import { WORK_STATUS_UI, normalizeWorkStatus } from "../domain/statusAdapters";
import {
  createProjectWorkItem,
  fetchProjectWorkItems,
  updateProjectWorkItem,
} from "../api/projectWorkItemsClient";
import {
  fetchWorkItemDeliverableStatus,
  generateWorkItemDeliverable,
  type WorkItemDeliverable,
  type WorkItemDeliverableStatus,
} from "../api/workItemDeliverablesClient";
import { createAppBundle, type AppBundleResponse } from "../api/appBundlesClient";
import { fetchWorkItemTemplates, type WorkItemTemplate } from "../api/workItemTemplatesClient";
import { fetchProjectRequirements, type ProjectRequirementRecord } from "../api/projectRequirementsClient";
import {
  JarvisRunSpecApiError,
  fetchJarvisWorkItemRunSpec,
  type JarvisRunSpecValidationReport,
} from "../api/jarvisRunSpecClient";
import { listJarvisRuns, triggerJarvisRun } from "../api/jarvisRunsClient";
import {
  buildDefinitionOfReady,
  describeMissingReadyItems,
  validationBadgeVariant,
  type DefinitionOfReadyResult,
  type JarvisRunSpecLike,
} from "../domain/jarvisRunSpec";
import { useOpenAiSecretStatus } from "../hooks/useOpenAiSecretStatus";
import { DEFAULT_WORKSPACE_ID } from "../config/workspace";

interface ProjectWorkplanTabProps {
  projectId: string;
  initialLinkedRequirementId?: string | null;
  shouldOpenCreate?: boolean;
  onRequestCloseCreate?: () => void;
}

interface WorkItemFormState {
  title: string;
  description: string;
  status: WorkStatus;
  impact: string;
  effort: string;
  roi: string;
  dependencies: string;
  owner: string;
  dueDate: string;
  linkedRequirementIds: string[];
  templateId: string;
}

interface WorkItemFormProps {
  visible: boolean;
  mode: "create" | "edit";
  requirements: ProjectRequirementRecord[];
  templates: WorkItemTemplate[];
  initialState: WorkItemFormState;
  isSaving: boolean;
  onCancel: () => void;
  onSubmit: (state: WorkItemFormState) => void;
  error?: string | null;
}

const JARVIS_ENVIRONMENTS = [
  { value: "sandbox", label: "Sandbox" },
  { value: "staging", label: "Staging" },
  { value: "dev", label: "Dev" },
];

const JARVIS_PROVIDERS = [
  { value: "stub", label: "Stub (no patches)" },
  { value: "rules", label: "Rules (deterministic)" },
  { value: "openai", label: "OpenAI (workspace secret or env)" },
];

const WORK_ITEM_DELIVERABLE_STATUS_LABELS: Record<WorkItemDeliverableStatus, string> = {
  created: "Created",
  updated: "Updated",
  unchanged: "Unchanged",
};

function formatDate(value?: string | null): string {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleDateString();
}

function formatDateTime(value?: string | null): string {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value ?? "-";
  return date.toLocaleString();
}

function formatDateInputValue(value?: string | null): string {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  return date.toISOString().slice(0, 10);
}

function formatNumberInput(value?: number | null): string {
  if (value === null || value === undefined) return "";
  return Number.isFinite(value) ? String(value) : "";
}

function parseNumberInput(value: string): number | null | undefined {
  const trimmed = value.trim();
  if (!trimmed) return null;
  const parsed = Number(trimmed);
  return Number.isFinite(parsed) ? parsed : undefined;
}

function formatDependenciesInput(value?: string[] | null): string {
  if (!value || value.length === 0) return "";
  return value.join(", ");
}

function parseDependenciesInput(value: string): string[] {
  if (!value.trim()) return [];
  return value
    .split(/[,\\n]/)
    .map((entry) => entry.trim())
    .filter(Boolean);
}

function jarvisRunBadge(
  status: JarvisRunRecord["status"],
): { label: string; variant: "default" | "success" | "warning" | "danger" } {
  switch (status) {
    case "succeeded":
      return { label: "Succeeded", variant: "success" };
    case "running":
      return { label: "Running", variant: "warning" };
    case "failed":
      return { label: "Failed", variant: "danger" };
    default:
      return { label: "Pending", variant: "warning" };
  }
}

function buildDeliverableNotice(
  item: ProjectWorkItem,
  deliverable: WorkItemDeliverable,
): { message: string; downloadUrl: string } {
  const missing = [
    deliverable.missingWorkItemId ? "Work Item ID" : null,
    ...deliverable.missingSections.map((section) => section.replace(/^##\s*/, "")),
  ].filter(Boolean);

  if (deliverable.status === "created") {
    return {
      message: `Deliverable generated for ${item.title}.`,
      downloadUrl: `/api/work-items/${encodeURIComponent(item.id)}/deliverable/download`,
    };
  }
  if (deliverable.status === "updated") {
    return {
      message: `Deliverable updated for ${item.title}.${missing.length ? ` Missing before: ${missing.join(", ")}.` : ""}`,
      downloadUrl: `/api/work-items/${encodeURIComponent(item.id)}/deliverable/download`,
    };
  }
  return {
    message: `Deliverable already complete for ${item.title}.`,
    downloadUrl: `/api/work-items/${encodeURIComponent(item.id)}/deliverable/download`,
  };
}

function ProjectWorkItemForm({
  visible,
  mode,
  requirements,
  templates,
  initialState,
  isSaving,
  onCancel,
  onSubmit,
  error,
}: WorkItemFormProps): JSX.Element | null {
  const [state, setState] = useState<WorkItemFormState>(initialState);

  useEffect(() => {
    setState(initialState);
  }, [initialState]);

  const toggleRequirement = (requirementId: string) => {
    setState((current) => {
      const exists = current.linkedRequirementIds.includes(requirementId);
      return {
        ...current,
        linkedRequirementIds: exists
          ? current.linkedRequirementIds.filter((id) => id !== requirementId)
          : [...current.linkedRequirementIds, requirementId],
      };
    });
  };

  const handleSubmit = (event: FormEvent) => {
    event.preventDefault();
    onSubmit(state);
  };

  if (!visible) return null;

  return (
    <div className="fixed inset-0 z-20 flex items-start justify-center bg-slate-900/50 p-4">
      <div className="mt-6 w-full max-w-3xl max-h-[90vh] overflow-y-auto">
        <Card>
          <form className="flex flex-col gap-4 p-6" onSubmit={handleSubmit}>
            <div className="flex items-start justify-between gap-4">
              <div>
                <PageHeader
                  title={mode === "edit" ? "Edit work item" : "Create work item"}
                  subtitle={
                    mode === "edit"
                      ? "Update task details and linked requirements."
                      : "Define a new task for this project and link it to requirements."
                  }
                />
              </div>
              <button type="button" className="button button-secondary" onClick={onCancel}>
                Close
              </button>
            </div>

            {error ? <div className="rounded bg-rose-50 p-3 text-sm text-rose-700">{error}</div> : null}

            <div className="grid gap-4 md:grid-cols-2">
              <div className="flex flex-col gap-2">
                <label className="text-sm font-semibold text-slate-700">Title *</label>
                <TextInput
                  required
                  value={state.title}
                  onChange={(event) => setState((current) => ({ ...current, title: event.target.value }))}
                  placeholder="Describe the task"
                />
              </div>

              <div className="flex flex-col gap-2">
                <label className="text-sm font-semibold text-slate-700">Template *</label>
                <SelectInput
                  required
                  value={state.templateId}
                  onChange={(event) =>
                    setState((current) => ({ ...current, templateId: event.target.value }))
                  }
                >
                  <option value="" disabled>
                    Select a template
                  </option>
                  {templates.map((template) => (
                    <option key={template.templateId} value={template.templateId}>
                      {template.name} ({template.workItemKind})
                    </option>
                  ))}
                </SelectInput>
                {templates.length === 0 ? (
                  <div className="text-xs text-slate-500">No templates available.</div>
                ) : null}
              </div>

              <div className="flex flex-col gap-2">
                <label className="text-sm font-semibold text-slate-700">Status</label>
                <SelectInput
                  value={state.status}
                  onChange={(event) =>
                    setState((current) => ({ ...current, status: event.target.value as WorkStatus }))
                  }
                >
                  {WORK_STATUSES.map((status) => (
                    <option key={status} value={status}>
                      {WORK_STATUS_UI[status].label}
                    </option>
                  ))}
                </SelectInput>
              </div>

              <div className="flex flex-col gap-2">
                <label className="text-sm font-semibold text-slate-700">Impact</label>
                <TextInput
                  type="number"
                  value={state.impact}
                  onChange={(event) => setState((current) => ({ ...current, impact: event.target.value }))}
                  placeholder="0"
                />
              </div>

              <div className="flex flex-col gap-2">
                <label className="text-sm font-semibold text-slate-700">Effort</label>
                <TextInput
                  type="number"
                  value={state.effort}
                  onChange={(event) => setState((current) => ({ ...current, effort: event.target.value }))}
                  placeholder="0"
                />
              </div>

              <div className="flex flex-col gap-2">
                <label className="text-sm font-semibold text-slate-700">ROI</label>
                <TextInput
                  type="number"
                  value={state.roi}
                  onChange={(event) => setState((current) => ({ ...current, roi: event.target.value }))}
                  placeholder="0"
                />
              </div>

              <div className="flex flex-col gap-2 md:col-span-2">
                <label className="text-sm font-semibold text-slate-700">Dependencies</label>
                <TextInput
                  value={state.dependencies}
                  onChange={(event) => setState((current) => ({ ...current, dependencies: event.target.value }))}
                  placeholder="Comma-separated WorkItem IDs"
                />
                <div className="text-xs text-slate-500">Use WorkItem IDs separated by commas.</div>
              </div>

              <div className="flex flex-col gap-2">
                <label className="text-sm font-semibold text-slate-700">Owner</label>
                <TextInput
                  value={state.owner}
                  onChange={(event) => setState((current) => ({ ...current, owner: event.target.value }))}
                  placeholder="e.g. Delivery lead"
                />
              </div>

              <div className="flex flex-col gap-2">
                <label className="text-sm font-semibold text-slate-700">Due date</label>
                <TextInput
                  type="date"
                  value={state.dueDate}
                  onChange={(event) => setState((current) => ({ ...current, dueDate: event.target.value }))}
                />
              </div>
            </div>

            <div className="flex flex-col gap-2">
              <label className="text-sm font-semibold text-slate-700">Description</label>
              <textarea
                className="block w-full rounded-md border border-slate-300 px-3 py-2 text-sm shadow-sm focus:border-slate-500 focus:ring-1 focus:ring-slate-500"
                rows={4}
                value={state.description}
                onChange={(event) => setState((current) => ({ ...current, description: event.target.value }))}
                placeholder="Add context or acceptance criteria"
              />
            </div>

            <div className="flex flex-col gap-3">
              <div>
                <div className="text-sm font-semibold text-slate-700">Linked requirements</div>
                <div className="text-sm text-slate-600">
                  Select the requirements this work item addresses.
                </div>
              </div>
              <div className="grid gap-2 md:grid-cols-2">
                {requirements.map((requirement) => (
                  <label key={requirement.id} className="flex items-start gap-2 rounded border border-slate-200 p-2 text-sm">
                    <CheckboxInput
                      checked={state.linkedRequirementIds.includes(String(requirement.id))}
                      onChange={() => toggleRequirement(String(requirement.id))}
                    />
                    <div>
                      <div className="font-semibold text-slate-800">{requirement.title ?? requirement.id}</div>
                      {requirement.description ? (
                        <div className="text-xs text-slate-600 line-clamp-2">{requirement.description}</div>
                      ) : null}
                    </div>
                  </label>
                ))}
                {requirements.length === 0 ? (
                  <div className="text-sm text-slate-600">No requirements found for this project.</div>
                ) : null}
              </div>
            </div>

            <div className="flex items-center justify-end gap-3 pt-2">
              <button type="button" className="button button-secondary" onClick={onCancel}>
                Cancel
              </button>
              <button type="submit" className="button button-primary" disabled={isSaving}>
                {isSaving ? "Saving..." : mode === "edit" ? "Save changes" : "Create work item"}
              </button>
            </div>
          </form>
        </Card>
      </div>
    </div>
  );
}

export function ProjectWorkplanTab({
  projectId,
  initialLinkedRequirementId,
  shouldOpenCreate,
  onRequestCloseCreate,
}: ProjectWorkplanTabProps): JSX.Element {
  const [workItems, setWorkItems] = useState<ProjectWorkItem[]>([]);
  const [requirements, setRequirements] = useState<ProjectRequirementRecord[]>([]);
  const [templates, setTemplates] = useState<WorkItemTemplate[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [formError, setFormError] = useState<string | null>(null);
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [editingItem, setEditingItem] = useState<ProjectWorkItem | null>(null);
  const [selectedWorkItemIds, setSelectedWorkItemIds] = useState<Set<string>>(new Set());
  const [bundleName, setBundleName] = useState<string>("");
  const [bundleDescription, setBundleDescription] = useState<string>("");
  const [bundleError, setBundleError] = useState<string | null>(null);
  const [bundleSuccess, setBundleSuccess] = useState<AppBundleResponse | null>(null);
  const [bundleGenerating, setBundleGenerating] = useState(false);
  const [jarvisSpecError, setJarvisSpecError] = useState<
    {
      message: string;
      remediationSteps: string[];
      workItemId?: string;
      workItemTitle?: string;
      validationReport?: JarvisRunSpecValidationReport;
      missingReadyItems?: string[];
    } | null
  >(null);
  const [jarvisSpecSuccess, setJarvisSpecSuccess] = useState<string | null>(null);
  const [jarvisSpecCache, setJarvisSpecCache] = useState<
    Record<string, { spec: JarvisRunSpecLike; validationReport: JarvisRunSpecValidationReport; definitionOfReady: DefinitionOfReadyResult }>
  >({});
  const [deliverableStatusById, setDeliverableStatusById] = useState<Record<string, WorkItemDeliverable>>({});
  const [deliverableLoadingId, setDeliverableLoadingId] = useState<string | null>(null);
  const [deliverableNotice, setDeliverableNotice] = useState<{ message: string; downloadUrl: string } | null>(null);
  const [deliverableError, setDeliverableError] = useState<string | null>(null);
  const [jarvisSpecLoadingId, setJarvisSpecLoadingId] = useState<string | null>(null);
  const [jarvisRunTargetId, setJarvisRunTargetId] = useState<string | null>(null);
  const [jarvisRuns, setJarvisRuns] = useState<JarvisRunRecord[]>([]);
  const [jarvisRunsLoading, setJarvisRunsLoading] = useState(false);
  const [jarvisRunError, setJarvisRunError] = useState<string | null>(null);
  const [jarvisRunLaunching, setJarvisRunLaunching] = useState(false);
  const [jarvisRunEnvironment, setJarvisRunEnvironment] = useState<string>(JARVIS_ENVIRONMENTS[0]?.value ?? "sandbox");
  const [jarvisRunDryRun, setJarvisRunDryRun] = useState<boolean>(true);
  const [jarvisRunProvider, setJarvisRunProvider] = useState<string>("stub");
  const [jarvisRunNotice, setJarvisRunNotice] = useState<string | null>(null);
  const [jarvisRunStatusFilter, setJarvisRunStatusFilter] = useState<JarvisRunStatus | "all">("all");
  const [jarvisRunFrom, setJarvisRunFrom] = useState<string>("");
  const [jarvisRunTo, setJarvisRunTo] = useState<string>("");
  const [requirementFilterId, setRequirementFilterId] = useState<string>("all");
  const [copiedWorkItemId, setCopiedWorkItemId] = useState<string | null>(null);
  const { status: openAiStatus, isLoading: secretsLoading, error: secretError, refreshStatus: refreshSecretStatus } = useOpenAiSecretStatus();

  const initialLinkedRequirementIds = useMemo(
    () => (initialLinkedRequirementId ? [initialLinkedRequirementId] : []),
    [initialLinkedRequirementId],
  );

  useEffect(() => {
    setRequirementFilterId(initialLinkedRequirementId ?? "all");
  }, [initialLinkedRequirementId]);

  const refreshList = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const [items, reqs, templateList] = await Promise.all([
        fetchProjectWorkItems(projectId),
        fetchProjectRequirements(projectId),
        fetchWorkItemTemplates(),
      ]);
      setWorkItems(
        items.map((item) => ({
          ...item,
          status: normalizeWorkStatus((item as ProjectWorkItem).status) ?? "not_started",
        })),
      );
      setRequirements(reqs);
      setTemplates(templateList);
    } catch (fetchError) {
      const message = fetchError instanceof Error ? fetchError.message : "Failed to load workplan";
      setError(message);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    void refreshList();
  }, [projectId]);

  useEffect(() => {
    if (workItems.length && !jarvisRunTargetId) {
      setJarvisRunTargetId(workItems[0].id);
    }
  }, [jarvisRunTargetId, workItems]);

  useEffect(() => {
    setSelectedWorkItemIds((current) => {
      const next = new Set<string>();
      workItems.forEach((item) => {
        if (current.has(item.id)) next.add(item.id);
      });
      return next;
    });
  }, [workItems]);

  const loadJarvisRuns = useCallback(async () => {
    if (!jarvisRunTargetId) return;
    setJarvisRunsLoading(true);
    setJarvisRunError(null);
    try {
      const runs = await listJarvisRuns({
        workItemId: jarvisRunTargetId,
        statuses: jarvisRunStatusFilter === "all" ? undefined : [jarvisRunStatusFilter],
        from: jarvisRunFrom || undefined,
        to: jarvisRunTo || undefined,
      });
      setJarvisRuns(runs);
    } catch (fetchError) {
      const message = fetchError instanceof Error ? fetchError.message : "Failed to load Jarvis runs";
      setJarvisRunError(message);
    } finally {
      setJarvisRunsLoading(false);
    }
  }, [jarvisRunFrom, jarvisRunStatusFilter, jarvisRunTargetId, jarvisRunTo]);

  useEffect(() => {
    if (jarvisRunTargetId) {
      void loadJarvisRuns();
    }
  }, [jarvisRunTargetId, loadJarvisRuns]);

  useEffect(() => {
    if (shouldOpenCreate && !isFormOpen) {
      setEditingItem(null);
      setIsFormOpen(true);
      onRequestCloseCreate?.();
    }
  }, [shouldOpenCreate, isFormOpen, onRequestCloseCreate]);

  const toggleWorkItemSelection = useCallback((id: string) => {
    setSelectedWorkItemIds((current) => {
      const next = new Set(current);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  }, []);

  const requirementTitleMap = useMemo(() => {
    const map = new Map<string, string>();
    requirements.forEach((req) => {
      map.set(String(req.id), req.title ?? String(req.id));
    });
    return map;
  }, [requirements]);

  const workItemCountByRequirementId = useMemo(() => {
    const map = new Map<string, number>();
    workItems.forEach((item) => {
      (item.linkedRequirementIds ?? []).forEach((requirementId) => {
        const key = String(requirementId);
        map.set(key, (map.get(key) ?? 0) + 1);
      });
    });
    return map;
  }, [workItems]);

  const filteredWorkItems = useMemo(() => {
    if (requirementFilterId === "all") return workItems;
    return workItems.filter((item) => (item.linkedRequirementIds ?? []).some((id) => String(id) === requirementFilterId));
  }, [requirementFilterId, workItems]);

  const filteredSelectedCount = useMemo(
    () => filteredWorkItems.filter((item) => selectedWorkItemIds.has(item.id)).length,
    [filteredWorkItems, selectedWorkItemIds],
  );

  const toggleAllWorkItems = useCallback(
    (checked: boolean) => {
      setSelectedWorkItemIds((current) => {
        const next = new Set(current);
        filteredWorkItems.forEach((item) => {
          if (checked) next.add(item.id);
          else next.delete(item.id);
        });
        return next;
      });
    },
    [filteredWorkItems],
  );

  const selectedWorkItems = useMemo(
    () => workItems.filter((item) => selectedWorkItemIds.has(item.id)),
    [selectedWorkItemIds, workItems],
  );

  const selectedCount = selectedWorkItemIds.size;
  const allSelected = filteredWorkItems.length > 0 && filteredSelectedCount === filteredWorkItems.length;

  const openCreateForm = () => {
    setFormError(null);
    setEditingItem(null);
    setIsFormOpen(true);
  };

  const closeForm = () => {
    setFormError(null);
    setIsFormOpen(false);
    setEditingItem(null);
    onRequestCloseCreate?.();
  };

  const handleSubmit = async (state: WorkItemFormState) => {
    if (!state.title.trim()) {
      setFormError("Title is required");
      return;
    }
    if (!state.templateId) {
      setFormError("Template is required");
      return;
    }

    const impact = parseNumberInput(state.impact);
    if (impact === undefined) {
      setFormError("Impact must be a number.");
      return;
    }
    const effort = parseNumberInput(state.effort);
    if (effort === undefined) {
      setFormError("Effort must be a number.");
      return;
    }
    const roi = parseNumberInput(state.roi);
    if (roi === undefined) {
      setFormError("ROI must be a number.");
      return;
    }

    setIsSaving(true);
    setFormError(null);

    const payload = {
      title: state.title.trim(),
      description: state.description.trim() ? state.description.trim() : undefined,
      status: state.status,
      impact,
      effort,
      roi,
      dependencies: parseDependenciesInput(state.dependencies),
      owner: state.owner.trim() ? state.owner.trim() : undefined,
      dueDate: state.dueDate ? state.dueDate : undefined,
      linkedRequirementIds: state.linkedRequirementIds,
      templateId: state.templateId,
    };

    try {
      if (editingItem) {
        const updated = await updateProjectWorkItem(projectId, editingItem.id, payload);
        setWorkItems((current) => current.map((item) => (item.id === updated.id ? updated : item)));
        setJarvisSpecCache((current) => {
          const next = { ...current };
          delete next[editingItem.id];
          return next;
        });
      } else {
        const created = await createProjectWorkItem(projectId, payload);
        setWorkItems((current) => [...current, created]);
        await runGenerateDeliverable(created);
      }
      closeForm();
    } catch (submitError) {
      const message = submitError instanceof Error ? submitError.message : "Failed to save work item";
      setFormError(message);
    } finally {
      setIsSaving(false);
    }
  };

  const handleGenerateAppBundle = async () => {
    if (selectedCount === 0) {
      setBundleError("Select at least one work item to generate an app bundle.");
      return;
    }

    const confirmed = window.confirm(`Generate an App Bundle for ${selectedCount} selected WorkItem(s)?`);
    if (!confirmed) return;

    setBundleGenerating(true);
    setBundleError(null);
    setBundleSuccess(null);

    try {
      const result = await createAppBundle(projectId, {
        name: bundleName.trim() ? bundleName.trim() : undefined,
        description: bundleDescription.trim() ? bundleDescription.trim() : undefined,
        workItemIds: Array.from(selectedWorkItemIds),
      });
      setBundleSuccess(result);
    } catch (generateError) {
      const message = generateError instanceof Error ? generateError.message : "Failed to generate app bundle";
      setBundleError(message);
    } finally {
      setBundleGenerating(false);
    }
  };

  const runGenerateDeliverable = useCallback(
    async (item: ProjectWorkItem, options: { refresh?: boolean } = {}): Promise<boolean> => {
      setDeliverableError(null);
      setDeliverableNotice(null);
      setDeliverableLoadingId(item.id);

      try {
        const deliverable = await generateWorkItemDeliverable(item.id, { refresh: options.refresh });
        setDeliverableStatusById((current) => ({ ...current, [item.id]: deliverable }));
        setDeliverableNotice(buildDeliverableNotice(item, deliverable));
        return true;
      } catch (deliverableError) {
        const message =
          deliverableError instanceof Error
            ? `Deliverable generation failed: ${deliverableError.message}`
            : "Deliverable generation failed.";
        setDeliverableError(message);
        return false;
      } finally {
        setDeliverableLoadingId(null);
      }
    },
    [],
  );

  const persistJarvisState = (
    item: ProjectWorkItem,
    spec: JarvisRunSpecLike,
    validationReport: JarvisRunSpecValidationReport,
  ): { spec: JarvisRunSpecLike; validationReport: JarvisRunSpecValidationReport; definitionOfReady: DefinitionOfReadyResult } => {
    const definitionOfReady = buildDefinitionOfReady(spec, item);
    setJarvisSpecCache((current) => ({ ...current, [item.id]: { spec, validationReport, definitionOfReady } }));
    return { spec, validationReport, definitionOfReady };
  };

  const exportJarvisSpec = async (
    item: ProjectWorkItem,
  ): Promise<{ spec: JarvisRunSpecLike; validationReport: JarvisRunSpecValidationReport; definitionOfReady: DefinitionOfReadyResult } | null> => {
    setJarvisSpecError(null);
    setJarvisSpecSuccess(null);
    setJarvisSpecLoadingId(item.id);

    try {
      const payload = await fetchJarvisWorkItemRunSpec(item.id);
      return persistJarvisState(item, (payload.spec ?? {}) as JarvisRunSpecLike, payload.validationReport);
    } catch (fetchError) {
      if (fetchError instanceof JarvisRunSpecApiError) {
        const validationReport = fetchError.validationReport;
        const spec = (fetchError.spec ?? {}) as JarvisRunSpecLike;
        const cachedState = validationReport ? persistJarvisState(item, spec, validationReport) : null;
        const missingItems = cachedState?.definitionOfReady ? describeMissingReadyItems(cachedState.definitionOfReady) : [];

        setJarvisSpecError({
          message: fetchError.message,
          remediationSteps: fetchError.remediationSteps ?? [],
          workItemId: item.id,
          workItemTitle: item.title,
          validationReport,
          missingReadyItems: missingItems.length ? missingItems : undefined,
        });
        return null;
      }

      const message = fetchError instanceof Error ? fetchError.message : "Failed to export Jarvis run spec";
      setJarvisSpecError({ message, remediationSteps: [], workItemId: item.id, workItemTitle: item.title });
      return null;
    } finally {
      setJarvisSpecLoadingId(null);
    }
  };

  const ensureJarvisReady = (
    item: ProjectWorkItem,
    state: { validationReport: JarvisRunSpecValidationReport; definitionOfReady: DefinitionOfReadyResult },
  ): boolean => {
    if (!state.validationReport.valid) {
      setJarvisSpecError({
        message: "Jarvis Run Spec failed validation.",
        remediationSteps: ["Download the validation report to review the schema errors.", "Update the WorkItem Jarvis fields and retry."],
        workItemId: item.id,
        workItemTitle: item.title,
        validationReport: state.validationReport,
      });
      return false;
    }

    if (!state.definitionOfReady.ready) {
      const missingItems = describeMissingReadyItems(state.definitionOfReady);
      setJarvisSpecError({
        message: "Jarvis export blocked: please complete the Definition of Ready.",
        remediationSteps: missingItems,
        workItemId: item.id,
        workItemTitle: item.title,
        validationReport: state.validationReport,
        missingReadyItems: missingItems,
      });
      return false;
    }

    return true;
  };

  const handleCopyJarvisSpec = async (event: MouseEvent, item: ProjectWorkItem) => {
    event.stopPropagation();
    const cached = jarvisSpecCache[item.id];
    const state = cached ?? (await exportJarvisSpec(item));
    if (!state) return;

    if (!ensureJarvisReady(item, state)) return;

    try {
      if (!navigator?.clipboard) {
        setJarvisSpecError({
          message: "Clipboard is not available in this browser.",
          remediationSteps: ["Use Download JSON instead or enable clipboard access."],
          workItemId: item.id,
          workItemTitle: item.title,
          validationReport: state.validationReport,
        });
        return;
      }
      await navigator.clipboard.writeText(JSON.stringify(state.spec, null, 2));
      setJarvisSpecSuccess(`Copied Jarvis Run Spec for ${item.title}`);
    } catch (clipboardError) {
      const message =
        clipboardError instanceof Error ? clipboardError.message : "Unable to copy Jarvis Run Spec to clipboard";
      setJarvisSpecError({
        message,
        remediationSteps: ["Allow clipboard access or use the Download JSON option instead."],
        workItemId: item.id,
        workItemTitle: item.title,
        validationReport: state.validationReport,
      });
    }
  };

  const handleDownloadJarvisSpec = async (event: MouseEvent, item: ProjectWorkItem) => {
    event.stopPropagation();
    const cached = jarvisSpecCache[item.id];
    const state = cached ?? (await exportJarvisSpec(item));

    if (!state) return;

    if (!ensureJarvisReady(item, state)) return;

    const blob = new Blob([JSON.stringify(state.spec, null, 2)], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `jarvis-run-spec-${item.id}.json`;
    link.click();
    URL.revokeObjectURL(url);
    setJarvisSpecSuccess(`Downloaded Jarvis Run Spec for ${item.title}`);
  };

  const downloadValidationReportFile = (workItemId: string, report: JarvisRunSpecValidationReport) => {
    const blob = new Blob([JSON.stringify(report, null, 2)], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `jarvis-run-spec-${workItemId}-validation-report.json`;
    link.click();
    URL.revokeObjectURL(url);
  };

  const handleDownloadValidationReport = (event: MouseEvent, item: ProjectWorkItem) => {
    event.stopPropagation();
    const report = jarvisSpecCache[item.id]?.validationReport ?? jarvisSpecError?.validationReport;
    if (!report) return;

    downloadValidationReportFile(item.id, report);
  };

  const handleGenerateDeliverable = useCallback(
    (event: MouseEvent, item: ProjectWorkItem) => {
      event.stopPropagation();
      const confirmed = window.confirm(
        `Generate/refresh the deliverable for "${item.title}" from the template? This will overwrite the existing deliverable.`,
      );
      if (!confirmed) return;
      void runGenerateDeliverable(item, { refresh: true });
    },
    [runGenerateDeliverable],
  );

  const ensureDeliverableExists = useCallback(
    async (item: ProjectWorkItem): Promise<boolean> => {
      try {
        const status = await fetchWorkItemDeliverableStatus(item.id);
        if (status.exists) return true;

        const confirmed = window.confirm(
          `No deliverable found at ${status.deliverablePath} for "${item.title}". Generate it now?`,
        );
        if (!confirmed) {
          setJarvisRunError("Jarvis run blocked: WorkItem deliverable is missing.");
          return false;
        }

        const created = await runGenerateDeliverable(item);
        if (!created) {
          setJarvisRunError("Jarvis run blocked: deliverable generation failed.");
          return false;
        }

        return true;
      } catch (error) {
        const message = error instanceof Error ? error.message : "Failed to check deliverable status";
        setJarvisRunError(message);
        return false;
      }
    },
    [runGenerateDeliverable],
  );

  const handleLaunchJarvisRun = async () => {
    if (!jarvisRunTargetId) return;
    const item = workItems.find((entry) => entry.id === jarvisRunTargetId);
    if (!item) return;

    setJarvisRunError(null);
    setJarvisRunNotice(null);

    const cached = jarvisSpecCache[item.id] ?? (await exportJarvisSpec(item));
    if (!cached) return;
    if (!ensureJarvisReady(item, cached)) return;

    if (!(await ensureDeliverableExists(item))) return;

    if (jarvisRunProvider === "openai" && openAiStatus && !openAiStatus.configured) {
      setJarvisRunError("OPENAI_API_KEY missing. Add it in Settings → Workspace settings → Secrets or as a server env var.");
      return;
    }

    const environmentLabel =
      JARVIS_ENVIRONMENTS.find((env) => env.value === jarvisRunEnvironment)?.label ?? jarvisRunEnvironment;
    const confirmed = window.confirm(
      `Launch Jarvis ${jarvisRunDryRun ? "dry-run" : "run"} for "${item.title}" in ${environmentLabel}?\nLogs are redacted and truncated in the UI.`,
    );
    if (!confirmed) return;

    setJarvisRunLaunching(true);

    try {
      await triggerJarvisRun(item.id, {
        environment: jarvisRunEnvironment,
        spec: cached.spec,
        dryRun: jarvisRunDryRun,
        provider: jarvisRunProvider,
        workspaceId: DEFAULT_WORKSPACE_ID,
      });
      setJarvisRunNotice(`Jarvis ${jarvisRunDryRun ? "dry-run" : "run"} queued for ${item.title}.`);
      void loadJarvisRuns();
      if (jarvisRunProvider === "openai") {
        void refreshSecretStatus();
      }
    } catch (launchError) {
      const message = launchError instanceof Error ? launchError.message : "Failed to start Jarvis run";
      setJarvisRunError(message);
    } finally {
      setJarvisRunLaunching(false);
    }
  };

  const handleCopyWorkItemId = useCallback(
    async (event: MouseEvent<HTMLButtonElement>, workItemId: string) => {
      event.stopPropagation();
      try {
        if (navigator?.clipboard?.writeText) {
          await navigator.clipboard.writeText(workItemId);
        }
        setCopiedWorkItemId(workItemId);
        window.setTimeout(() => {
          setCopiedWorkItemId((current) => (current === workItemId ? null : current));
        }, 1200);
      } catch {
        setCopiedWorkItemId(workItemId);
      }
    },
    [],
  );

  const columns = useMemo<DataTableColumn<ProjectWorkItem>[]>(
    () => [
      {
        id: "select",
        header: (
          <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-slate-500">
            <CheckboxInput
              checked={allSelected}
              onChange={(event) => toggleAllWorkItems(event.target.checked)}
              onClick={(event) => event.stopPropagation()}
            />
            <span>Select</span>
          </div>
        ),
        accessor: (row) => (
          <CheckboxInput
            checked={selectedWorkItemIds.has(row.id)}
            onChange={() => toggleWorkItemSelection(row.id)}
            onClick={(event) => event.stopPropagation()}
          />
        ),
      },
      {
        id: "title",
        header: "Title",
        accessor: (row) => (
          <div className="flex flex-col gap-1">
            <div className="font-semibold text-slate-900">{row.title}</div>
            <div className="flex flex-wrap items-center gap-2 text-xs text-slate-600">
              <span className="font-mono">ID: {row.id}</span>
              <button
                type="button"
                className="button button-ghost button-xs"
                onClick={(event) => void handleCopyWorkItemId(event, row.id)}
              >
                {copiedWorkItemId === row.id ? "Copied" : "Copy ID"}
              </button>
            </div>
          </div>
        ),
      },
      {
        id: "status",
        header: "Status",
        accessor: (row) => {
          const badge = WORK_STATUS_UI[row.status] ?? WORK_STATUS_UI.not_started;
          return <Badge label={badge.label} variant={badge.variant} />;
        },
      },
      {
        id: "impact",
        header: "Impact",
        accessor: (row) => (row.impact ?? "-"),
      },
      {
        id: "effort",
        header: "Effort",
        accessor: (row) => (row.effort ?? "-"),
      },
      {
        id: "roi",
        header: "ROI",
        accessor: (row) => (row.roi ?? "-"),
      },
      {
        id: "owner",
        header: "Owner",
        accessor: (row) => row.owner ?? "-",
      },
      {
        id: "dueDate",
        header: "Due date",
        accessor: (row) => formatDate(row.dueDate),
      },
      {
        id: "linked",
        header: "Linked requirements",
        accessor: (row) => {
          const ids = row.linkedRequirementIds ?? [];
          if (ids.length === 0) return "-";
          const labels = ids
            .map((id: string) => requirementTitleMap.get(String(id)) ?? String(id))
            .filter(Boolean);
          if (labels.length <= 2) {
            return labels.join(", ");
          }
          return `${labels.slice(0, 2).join(", ")} +${labels.length - 2}`;
        },
      },
      {
        id: "deliverable",
        header: "Deliverable",
        accessor: (row) => {
          const status = deliverableStatusById[row.id];
          const downloadUrl = `/api/work-items/${encodeURIComponent(row.id)}/deliverable/download`;
          return (
            <div className="flex flex-col gap-2">
              <button
                type="button"
                className="button button-ghost button-xs whitespace-nowrap"
                disabled={deliverableLoadingId === row.id}
                onClick={(event) => handleGenerateDeliverable(event, row)}
              >
                {deliverableLoadingId === row.id ? "Working..." : "Generate deliverable"}
              </button>
              <a className="text-xs text-blue-700 underline" href={downloadUrl}>
                Download deliverable
              </a>
              <div className="text-xs text-slate-600">
                {status ? WORK_ITEM_DELIVERABLE_STATUS_LABELS[status.status] : "Not generated"}
              </div>
            </div>
          );
        },
      },
      {
        id: "jarvis",
        header: "Jarvis",
        accessor: (row) => (
          <div className="flex flex-col gap-2">
            <div className="flex flex-wrap items-center gap-2 text-xs text-slate-700">
              <Badge
                label={
                  jarvisSpecCache[row.id]?.validationReport
                    ? jarvisSpecCache[row.id]?.validationReport.valid
                      ? "Validation PASS"
                      : "Validation FAIL"
                    : "Awaiting validation"
                }
                variant={validationBadgeVariant(jarvisSpecCache[row.id]?.validationReport)}
              />
              {jarvisSpecCache[row.id]?.validationReport ? (
                <button
                  type="button"
                  className="text-blue-700 underline"
                  onClick={(event) => handleDownloadValidationReport(event, row)}
                >
                  Download report
                </button>
              ) : (
                <span className="text-slate-500">Run an export to validate</span>
              )}
            </div>

            <div className="rounded border border-slate-200 bg-slate-50 p-2 text-[11px] text-slate-700">
              <div className="font-semibold text-slate-800">Definition of Ready</div>
              {jarvisSpecCache[row.id]?.definitionOfReady ? (
                <ul className="mt-1 space-y-1">
                  {jarvisSpecCache[row.id]?.definitionOfReady.items.map((itemState) => (
                    <li key={itemState.id} className="flex items-center gap-1">
                      <span
                        className={`h-2 w-2 rounded-full ${itemState.satisfied ? "bg-green-500" : "bg-amber-500"}`}
                        aria-hidden
                      />
                      <span className={itemState.satisfied ? "text-slate-700" : "text-amber-700"}>{itemState.label}</span>
                    </li>
                  ))}
                </ul>
              ) : (
                <div className="mt-1 text-slate-600">Export to compute readiness.</div>
              )}
            </div>

            <div className="flex flex-wrap items-center gap-2">
              <button
                type="button"
                className="button button-secondary button-xs whitespace-nowrap"
                disabled={jarvisSpecLoadingId === row.id}
                onClick={(event) => void handleCopyJarvisSpec(event, row)}
              >
                {jarvisSpecLoadingId === row.id ? "Copying..." : "Copy Jarvis Run Spec"}
              </button>
              <button
                type="button"
                className="button button-ghost button-xs whitespace-nowrap"
                disabled={jarvisSpecLoadingId === row.id}
                onClick={(event) => void handleDownloadJarvisSpec(event, row)}
              >
                {jarvisSpecLoadingId === row.id ? "Working..." : "Download Jarvis Run Spec"}
              </button>
            </div>
          </div>
        ),
      },
    ],
    [
      allSelected,
      copiedWorkItemId,
      deliverableLoadingId,
      deliverableStatusById,
      handleCopyWorkItemId,
      handleCopyJarvisSpec,
      handleGenerateDeliverable,
      handleDownloadJarvisSpec,
      handleDownloadValidationReport,
      jarvisSpecCache,
      jarvisSpecLoadingId,
      requirementTitleMap,
      selectedWorkItemIds,
      toggleAllWorkItems,
      toggleWorkItemSelection,
    ],
  );

  const defaultLinkedRequirementIds = useMemo(
    () => (requirementFilterId !== "all" ? [requirementFilterId] : initialLinkedRequirementIds),
    [initialLinkedRequirementIds, requirementFilterId],
  );

  const initialFormState: WorkItemFormState = useMemo(
    () => ({
      title: editingItem?.title ?? "",
      description: editingItem?.description ?? "",
      status: normalizeWorkStatus(editingItem?.status) ?? "not_started",
      impact: formatNumberInput(editingItem?.impact),
      effort: formatNumberInput(editingItem?.effort),
      roi: formatNumberInput(editingItem?.roi),
      dependencies: formatDependenciesInput(editingItem?.dependencies),
      owner: editingItem?.owner ?? "",
      dueDate: formatDateInputValue(editingItem?.dueDate ?? null),
      linkedRequirementIds: editingItem?.linkedRequirementIds ?? defaultLinkedRequirementIds,
      templateId: editingItem?.templateId ?? "",
    }),
    [defaultLinkedRequirementIds, editingItem],
  );

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <PageHeader
            title="Project workplan"
            subtitle="Plan tasks and link them to requirements for this project."
          />
        </div>
        <button type="button" className="button button-primary" onClick={openCreateForm}>
          Add work item
        </button>
      </div>

      {jarvisSpecError ? (
        <div className="rounded border border-amber-300 bg-amber-50 p-4 text-sm text-amber-900">
          <div className="font-semibold">Jarvis Run Spec unavailable</div>
          <div className="mt-1">{jarvisSpecError.message}</div>
          {jarvisSpecError.validationReport ? (
            <div className="mt-2 flex items-center gap-2 text-xs">
              <Badge
                label={jarvisSpecError.validationReport.valid ? "Validation PASS" : "Validation FAIL"}
                variant={validationBadgeVariant(jarvisSpecError.validationReport)}
              />
              <button
                type="button"
                className="text-blue-700 underline"
                onClick={() =>
                  downloadValidationReportFile(
                    jarvisSpecError.workItemId ?? "jarvis-run-spec",
                    jarvisSpecError.validationReport!,
                  )
                }
              >
                Download validation report
              </button>
            </div>
          ) : null}
          {jarvisSpecError.remediationSteps.length ? (
            <ul className="ml-4 mt-2 list-disc space-y-1">
              {jarvisSpecError.remediationSteps.map((step) => (
                <li key={step}>{step}</li>
              ))}
            </ul>
          ) : null}
          {jarvisSpecError.missingReadyItems?.length ? (
            <div className="mt-2 text-xs text-amber-800">
              <div className="font-semibold">Definition of Ready blockers</div>
              <ul className="ml-4 mt-1 list-disc space-y-1">
                {jarvisSpecError.missingReadyItems.map((item) => (
                  <li key={item}>{item}</li>
                ))}
              </ul>
            </div>
          ) : null}
          {jarvisSpecError.workItemId ? (
            <div className="mt-3">
              <button
                type="button"
                className="button button-ghost button-xs"
                onClick={() => {
                  const target = workItems.find((item) => item.id === jarvisSpecError.workItemId);
                  if (target) {
                    setEditingItem(target);
                    setIsFormOpen(true);
                  }
                }}
              >
                Open WorkItem to fix Jarvis fields
              </button>
            </div>
          ) : null}
          <div className="mt-2 text-xs text-amber-800">
            Tip: add Jarvis policies and acceptance criteria to this WorkItem, then retry.
          </div>
        </div>
      ) : null}

      {jarvisSpecSuccess ? (
        <div className="rounded border border-green-300 bg-green-50 p-3 text-sm text-green-900">
          {jarvisSpecSuccess}
        </div>
      ) : null}

      {deliverableNotice ? (
        <div className="rounded border border-blue-200 bg-blue-50 p-3 text-sm text-blue-900">
          <div>{deliverableNotice.message}</div>
          <a className="mt-1 inline-flex text-blue-700 underline" href={deliverableNotice.downloadUrl}>
            Open deliverable
          </a>
        </div>
      ) : null}

      {deliverableError ? (
        <div className="rounded border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900">{deliverableError}</div>
      ) : null}

      <Card>
        <div className="flex flex-wrap items-center gap-3 p-4">
          <div className="text-sm font-semibold text-slate-700">Requirement focus</div>
          <SelectInput
            className="w-full md:w-[480px]"
            value={requirementFilterId}
            onChange={(event) => setRequirementFilterId(event.target.value)}
          >
            <option value="all">All requirements</option>
            {requirements.map((requirement) => {
              const id = String(requirement.id);
              const count = workItemCountByRequirementId.get(id) ?? 0;
              return (
                <option key={id} value={id}>
                  {requirement.title ?? id} ({count})
                </option>
              );
            })}
          </SelectInput>
          {requirementFilterId !== "all" ? (
            <button
              type="button"
              className="button button-secondary button-sm"
              onClick={() => setRequirementFilterId("all")}
            >
              Clear
            </button>
          ) : null}
          <div className="text-xs text-slate-600">
            Showing {filteredWorkItems.length} work item{filteredWorkItems.length > 1 ? "s" : ""}.
          </div>
        </div>
      </Card>

      <Card>
        <div className="p-4">
          <DataTable<ProjectWorkItem>
            columns={columns}
            data={filteredWorkItems}
            isLoading={isLoading}
            error={error ? <span className="text-sm text-rose-600">{error}</span> : undefined}
            pageSize={filteredWorkItems.length || 10}
            totalCount={filteredWorkItems.length}
            onRowClick={(row) => {
              setEditingItem(row);
              setIsFormOpen(true);
            }}
            emptyState={<EmptyState title="No work items" description="Add tasks to get started." />}
          />
        </div>
      </Card>

      <Card>
        <div className="flex flex-col gap-4 p-4">
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div>
              <div className="text-base font-semibold text-slate-900">App bundle assistant</div>
              <p className="text-sm text-slate-700">
                Generate a packaged App Bundle from the WorkItems you select above.
              </p>
            </div>
            <div className="rounded-full bg-slate-100 px-3 py-1 text-xs font-semibold text-slate-700">
              Selected: {selectedCount}
            </div>
          </div>

          {bundleError ? (
            <div className="rounded border border-rose-200 bg-rose-50 p-3 text-sm text-rose-800">{bundleError}</div>
          ) : null}
          {bundleSuccess ? (
            <div className="rounded border border-green-200 bg-green-50 p-3 text-sm text-green-800">
              Bundle generated: {bundleSuccess.bundle.name ?? bundleSuccess.bundle.id}
            </div>
          ) : null}

          <div className="grid gap-4 lg:grid-cols-[1.1fr_0.9fr]">
            <div className="flex flex-col gap-3">
              <div className="flex flex-col gap-2">
                <label className="text-sm font-semibold text-slate-700">Bundle name</label>
                <TextInput
                  value={bundleName}
                  onChange={(event) => setBundleName(event.target.value)}
                  placeholder="Optional label for this App Bundle"
                />
              </div>

              <div className="flex flex-col gap-2">
                <label className="text-sm font-semibold text-slate-700">Bundle notes</label>
                <textarea
                  className="block w-full rounded-md border border-slate-300 px-3 py-2 text-sm shadow-sm focus:border-slate-500 focus:ring-1 focus:ring-slate-500"
                  rows={3}
                  value={bundleDescription}
                  onChange={(event) => setBundleDescription(event.target.value)}
                  placeholder="Describe the release intent or deployment notes."
                />
              </div>

              <button
                type="button"
                className="button button-primary self-start"
                disabled={selectedCount === 0 || bundleGenerating}
                onClick={() => void handleGenerateAppBundle()}
              >
                {bundleGenerating ? "Generating..." : "Generate App Bundle"}
              </button>
              <div className="text-xs text-slate-600">
                The bundle includes a manifest plus a downloadable archive of the selected WorkItems.
              </div>
            </div>

            <div className="rounded border border-slate-200 bg-slate-50 p-3 text-sm text-slate-700">
              <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">Selected WorkItems</div>
              {selectedWorkItems.length === 0 ? (
                <div className="mt-2 text-sm text-slate-600">No WorkItems selected yet.</div>
              ) : (
                <ul className="mt-2 space-y-2">
                  {selectedWorkItems.map((item) => (
                    <li key={item.id} className="flex items-center justify-between gap-2">
                      <div className="text-sm font-semibold text-slate-800">{item.title}</div>
                      <Badge
                        label={WORK_STATUS_UI[item.status]?.label ?? item.status}
                        variant={WORK_STATUS_UI[item.status]?.variant ?? "default"}
                      />
                    </li>
                  ))}
                </ul>
              )}

              {bundleSuccess ? (
                <div className="mt-4 border-t border-slate-200 pt-3 text-xs text-slate-700">
                  <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">Download</div>
                  <div className="mt-2 flex flex-col gap-1">
                    <a className="text-blue-700 underline" href={bundleSuccess.archiveUrl}>
                      Download App Bundle archive
                    </a>
                    <a className="text-blue-700 underline" href={bundleSuccess.bundleUrl}>
                      View bundle.json
                    </a>
                    <a className="text-blue-700 underline" href={bundleSuccess.manifestUrl}>
                      View manifest.json
                    </a>
                  </div>
                </div>
              ) : null}
            </div>
          </div>
        </div>
      </Card>

      <Card>
        <div className="flex flex-col gap-3 p-4">
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div>
              <div className="text-base font-semibold text-slate-900">Jarvis runs</div>
              <p className="text-sm text-slate-700">
                Queue guarded Jarvis runs per WorkItem. Environments exclude prod and UI logs are redacted tails only.
              </p>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <div className="text-xs font-semibold uppercase tracking-wide text-slate-600">Work item</div>
              <SelectInput
                value={jarvisRunTargetId ?? ""}
                onChange={(event) => setJarvisRunTargetId(event.target.value || null)}
              >
                {workItems.map((item) => (
                  <option key={item.id} value={item.id}>
                    {item.title}
                  </option>
                ))}
              </SelectInput>
              <button
                type="button"
                className="button button-ghost button-xs"
                disabled={!jarvisRunTargetId || jarvisRunsLoading}
                onClick={() => void loadJarvisRuns()}
              >
                {jarvisRunsLoading ? "Refreshing..." : "Refresh runs"}
              </button>
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-3">
            <div className="flex items-center gap-2">
              <div className="text-xs font-semibold uppercase tracking-wide text-slate-600">Status</div>
              <SelectInput
                value={jarvisRunStatusFilter}
                onChange={(event) => setJarvisRunStatusFilter((event.target.value as JarvisRunStatus | "all") ?? "all")}
              >
                <option value="all">All</option>
                <option value="pending">Pending</option>
                <option value="running">Running</option>
                <option value="succeeded">Succeeded</option>
                <option value="failed">Failed</option>
              </SelectInput>
            </div>
            <div className="flex items-center gap-2">
              <div className="text-xs font-semibold uppercase tracking-wide text-slate-600">From</div>
              <input
                type="date"
                className="input input-sm"
                value={jarvisRunFrom}
                onChange={(event) => setJarvisRunFrom(event.target.value)}
              />
            </div>
            <div className="flex items-center gap-2">
              <div className="text-xs font-semibold uppercase tracking-wide text-slate-600">To</div>
              <input
                type="date"
                className="input input-sm"
                value={jarvisRunTo}
                onChange={(event) => setJarvisRunTo(event.target.value)}
              />
            </div>
          </div>

          {jarvisRunError ? (
            <div className="rounded border border-rose-200 bg-rose-50 p-3 text-sm text-rose-800">{jarvisRunError}</div>
          ) : null}
          {secretError ? (
            <div className="rounded border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800">{secretError}</div>
          ) : null}
          {jarvisRunNotice ? (
            <div className="rounded border border-green-200 bg-green-50 p-3 text-sm text-green-800">{jarvisRunNotice}</div>
          ) : null}

          <div className="grid gap-4 lg:grid-cols-[0.95fr_1.05fr]">
            <div className="rounded border border-slate-200 bg-slate-50 p-3">
              <div className="flex items-center justify-between gap-2">
                <div className="text-sm font-semibold text-slate-900">Queue a run</div>
                <span className="text-[11px] text-slate-600">Prod/prod-like environments are blocked.</span>
              </div>
              <div className="mt-3 space-y-3 text-sm text-slate-800">
                <div className="flex flex-col gap-1">
                  <label className="text-xs font-semibold uppercase tracking-wide text-slate-600">Environment</label>
                  <SelectInput value={jarvisRunEnvironment} onChange={(event) => setJarvisRunEnvironment(event.target.value)}>
                    {JARVIS_ENVIRONMENTS.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </SelectInput>
                </div>

                <div className="flex flex-col gap-1">
                  <label className="text-xs font-semibold uppercase tracking-wide text-slate-600">Provider</label>
                  <SelectInput value={jarvisRunProvider} onChange={(event) => setJarvisRunProvider(event.target.value)}>
                    {JARVIS_PROVIDERS.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </SelectInput>
                  {jarvisRunProvider === "openai" ? (
                    <div className="flex flex-col gap-1 text-xs text-slate-700">
                      <div className="flex items-center gap-2">
                        <span>OpenAI API key status:</span>
                        <Badge
                          label={openAiStatus?.configured ? "Configured" : "Missing"}
                          variant={openAiStatus?.configured ? "success" : "danger"}
                        />
                      </div>
                      {openAiStatus?.previewMasked ? (
                        <div className="text-[11px] text-slate-600">Using secret/env ({openAiStatus.previewMasked}).</div>
                      ) : (
                        <div className="text-[11px] text-slate-600">
                          Add a key in Settings → Workspace settings → Secrets. Runs fail if the provider requires it.
                        </div>
                      )}
                      <button
                        type="button"
                        className="button button-ghost button-xs self-start"
                        onClick={() => void refreshSecretStatus()}
                        disabled={secretsLoading}
                      >
                        {secretsLoading ? "Checking..." : "Refresh status"}
                      </button>
                    </div>
                  ) : null}
                </div>

                <label className="flex items-center gap-2 text-sm">
                  <CheckboxInput checked={jarvisRunDryRun} onChange={(event) => setJarvisRunDryRun(event.target.checked)} />
                  <span className="text-slate-800">Run in dry-run mode (recommended for first pass)</span>
                </label>

                <button
                  type="button"
                  className="button button-primary"
                  disabled={!jarvisRunTargetId || jarvisRunLaunching}
                  onClick={() => void handleLaunchJarvisRun()}
                >
                  {jarvisRunLaunching ? "Launching..." : "Launch Jarvis run"}
                </button>
                <div className="text-xs text-slate-600">
                  Logs are redacted and truncated to avoid secrets/PII leaks. Download full artifacts if you need more context.
                </div>
              </div>
            </div>

            <div className="flex flex-col gap-2">
              <div className="flex flex-wrap items-baseline justify-between gap-2">
                <div className="text-sm font-semibold text-slate-900">Latest runs</div>
                <div className="text-[11px] text-slate-600">
                  Artifacts: console.log, logs.jsonl, run_summary.json, failure_report.json
                </div>
              </div>

              {jarvisRunsLoading ? (
                <div className="rounded border border-slate-200 bg-slate-50 p-3 text-sm text-slate-700">Loading runs…</div>
              ) : jarvisRuns.length === 0 ? (
                <div className="rounded border border-slate-200 bg-slate-50 p-3 text-sm text-slate-700">
                  No runs yet for this WorkItem. Export the Jarvis spec, confirm readiness, then launch a dry-run.
                </div>
              ) : (
                jarvisRuns.map((run) => {
                  const badge = jarvisRunBadge(run.status);
                  return (
                    <div key={run.id} className="rounded border border-slate-200 bg-white p-3 shadow-sm">
                      <div className="flex flex-wrap items-center justify-between gap-2">
                        <div className="flex flex-wrap items-center gap-2">
                          <Badge label={badge.label} variant={badge.variant} />
                          <div className="text-sm font-semibold text-slate-900">{run.title ?? `Run ${run.id}`}</div>
                        </div>
                        <div className="text-xs text-slate-600">
                          Started {formatDateTime(run.startedAt ?? run.createdAt)}{" "}
                          {run.finishedAt ? `• Finished ${formatDateTime(run.finishedAt)}` : ""}
                        </div>
                      </div>

                      <div className="mt-2 grid gap-2 text-xs text-slate-700 md:grid-cols-2">
                        <div>Environment: {run.environment}</div>
                        <div>Dry-run: {run.dryRun ? "Yes" : "No"}</div>
                        <div>Stop reason: {run.stopReason ?? "-"}</div>
                        <div>Iterations: {run.summary?.iterations ?? "-"}</div>
                        <div>Dispatch mode: {run.dispatch?.mode ?? "direct"}</div>
                        <div>Queue status: {run.dispatch?.queueStatus ?? "-"}</div>
                        <div>Claim owner: {run.dispatch?.claimOwner ?? "-"}</div>
                        <div>
                          Attempts:{" "}
                          {typeof run.dispatch?.attemptCount === "number"
                            ? `${run.dispatch.attemptCount}/${Math.max(run.dispatch.maxAttempts ?? run.dispatch.attemptCount, run.dispatch.attemptCount)}`
                            : "-"}
                        </div>
                        <div>Queued at: {run.dispatch?.queuedAt ? formatDateTime(run.dispatch.queuedAt) : "-"}</div>
                        <div>Claimed at: {run.dispatch?.claimedAt ? formatDateTime(run.dispatch.claimedAt) : "-"}</div>
                        <div>Heartbeat: {run.dispatch?.heartbeatAt ? formatDateTime(run.dispatch.heartbeatAt) : "-"}</div>
                        <div>
                          Queue lease: {run.dispatch?.leaseExpiresAt ? formatDateTime(run.dispatch.leaseExpiresAt) : "-"}
                        </div>
                        <div className="md:col-span-2">
                          Next retry: {run.dispatch?.nextRetryAt ? formatDateTime(run.dispatch.nextRetryAt) : "-"}
                        </div>
                      </div>

                      {run.dispatch?.mode === "queued" ? (
                        <div className="mt-2 rounded border border-slate-200 bg-slate-50 p-2 text-[11px] text-slate-700">
                          Queued runs are executed by the persistent Jarvis worker outside the HTTP request lifecycle.
                        </div>
                      ) : null}

                      <div className="mt-2 flex flex-wrap gap-3 text-xs">
                        {run.artifacts.consoleLog ? (
                          <a className="text-blue-700 underline" href={run.artifacts.consoleLog}>
                            Download console.log
                          </a>
                        ) : null}
                        {run.artifacts.structuredLog ? (
                          <a className="text-blue-700 underline" href={run.artifacts.structuredLog}>
                            Download logs.jsonl
                          </a>
                        ) : null}
                        {run.artifacts.runSummary ? (
                          <a className="text-blue-700 underline" href={run.artifacts.runSummary}>
                            Download run_summary.json
                          </a>
                        ) : null}
                        {run.artifacts.failureReport ? (
                          <a className="text-blue-700 underline" href={run.artifacts.failureReport}>
                            Download failure_report.json
                          </a>
                        ) : (
                          <span className="text-slate-500">No failure report captured</span>
                        )}
                      </div>

                      {run.logTail ? (
                        <div className="mt-2 rounded bg-slate-900 p-2 text-[11px] font-mono text-slate-100">
                          <div className="mb-1 text-[10px] uppercase tracking-wide text-slate-300">Log tail (redacted)</div>
                          <pre className="whitespace-pre-wrap break-words text-[11px] leading-4">{run.logTail}</pre>
                        </div>
                      ) : (
                        <div className="mt-2 text-[11px] text-slate-600">Log tail unavailable yet.</div>
                      )}
                    </div>
                  );
                })
              )}
            </div>
          </div>
        </div>
      </Card>

      <ProjectWorkItemForm
        visible={isFormOpen}
        mode={editingItem ? "edit" : "create"}
        requirements={requirements}
        templates={templates}
        initialState={initialFormState}
        isSaving={isSaving}
        error={formError}
        onCancel={closeForm}
        onSubmit={handleSubmit}
      />
    </div>
  );
}
